// NodeTitleButton.jsx

import React, { useCallback, useEffect, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './NodeTitleButton.css';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * Sizes a textarea to its scroll height after wrap styles are applied.
 *
 * @param {HTMLTextAreaElement|null} el
 */
const sizeTextareaToContent = (el) => {
  if (!el || typeof el.scrollHeight !== 'number') return;
  el.style.height = '0px';
  el.style.height = `${el.scrollHeight}px`;
  el.style.overflow = 'hidden';
};

/**
 * NodeTitle
 *
 * Displays the title section for a node or a button to add title if none exist.
 *
 * @param {object} node - Node object.
 * @param {Function} onTitleSaved - Callback when title is saved.
 * @param {boolean} hybrid - Whether to use hybrid transparent background mode.
 * @param {object} inputStyle - Optional style overrides for the title input.
 * @param {boolean} autoFocus - Whether to auto-focus the title input.
 * @param {boolean} autoSelect - Whether to auto-select the title input.
 * @param {boolean} [multiline=true] - Whether the title can wrap across lines.
 * @param {boolean} [expandable=true] - Whether a multiline title grows with content.
 * @param {boolean} [readOnly=false] - Whether the title is read-only.
 * @param {boolean} [autosizeWidth=false] - Hug text width (for title+subtitle clusters).
 * @param {boolean} [collapseUntilFocus=false] - Single-line ellipsis until focused, then wrap.
 * @param {function} [onFocus] - Optional focus handler.
 * @param {function} [onBlur] - Optional blur handler (runs after save).
 *
 * @returns {ReactNode} - NodeTitle component.
 */
const NodeTitleButton = ({
  node,
  onTitleSaved,
  hybrid = false,
  inputStyle = null,
  autoFocus = true,
  autoSelect = true,
  multiline = true,
  expandable = true,
  readOnly = false,
  autosizeWidth = false,
  collapseUntilFocus = false,
  onFocus,
  onBlur,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // Catalog-adopted rows: title is platform-owned (live-read).
    const isCatalogLocked = Boolean(String(node?.catalogId || '').trim());
    const effectiveReadOnly = readOnly || isCatalogLocked;

    // ----- REFS ----- //

    const containerRef = useRef(null);

    // ----- STATE ----- //

    // Title state

    const [title, setTitle] = useState(node?.title || '');
    // Collapse-until-focus: expand to wrap only while editing.
    const [isFocused, setIsFocused] = useState(false);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setTitle(node?.title || '');
    }, [node?.title]);

    // Remeasure after expand — TextInput may have sized while still collapsed.
    useEffect(() => {
        if (!collapseUntilFocus || !isFocused) return undefined;

        const el = containerRef.current?.querySelector?.('textarea.text-input-control');
        let frame2 = null;
        const frame1 = requestAnimationFrame(() => {
            sizeTextareaToContent(el);
            // Second frame: white-space wrap has fully applied.
            frame2 = requestAnimationFrame(() => sizeTextareaToContent(el));
        });

        return () => {
            cancelAnimationFrame(frame1);
            if (frame2) cancelAnimationFrame(frame2);
        };
    }, [collapseUntilFocus, isFocused, title]);

    // ----- HANDLERS ----- //

    /**
 * Handles title change.
 *
 * @param {*} event
 */

    const handleTitleChange = useCallback((event) => {
        if (effectiveReadOnly) return;
        setTitle(event?.target?.value ?? '');
    }, [effectiveReadOnly]);

    /**
 * Handles title focus.
 *
 * @param {FocusEvent} event
 */
    const handleTitleFocus = useCallback((event) => {
        if (collapseUntilFocus) setIsFocused(true);
        if (typeof onFocus === 'function') onFocus(event);
    }, [collapseUntilFocus, onFocus]);

    /**
 * Handles title blur.
 *
 * @param {FocusEvent} event
 */
    const handleTitleBlur = useCallback((event) => {
        if (collapseUntilFocus) setIsFocused(false);
        if (!effectiveReadOnly && typeof onTitleSaved === 'function') {
            const nextTitle = String(title || '').trim();
            onTitleSaved(nextTitle);
        }
        if (typeof onBlur === 'function') onBlur(event);
    }, [collapseUntilFocus, effectiveReadOnly, onBlur, onTitleSaved, title]);

    // ----- RENDER ----- //

    const placeholder = effectiveReadOnly ? '' : 'Add a title...';
    const isCollapsed = Boolean(collapseUntilFocus && !isFocused);
    // Keep element type stable (textarea) so focus is not lost when expanding.
    const effectiveMultiline = collapseUntilFocus ? true : multiline;
    const effectiveExpandable = collapseUntilFocus ? true : expandable;
    const useAutosize = Boolean(autosizeWidth) && !isMobile && !effectiveMultiline;
    const sizerText = title || placeholder || ' ';
    const resolvedInputStyle = {
        fontSize: 28,
        fontWeight: 700,
        paddingLeft: useAutosize ? 4 : 12,
        paddingTop: isMobile ? 5 : 4,
        paddingBottom: 4,
        width: '100%',
        maxWidth: useAutosize ? '100%' : (isMobile || collapseUntilFocus ? '100%' : '70%'),
        ...(inputStyle || {}),
        ...(useAutosize ? { width: '100%', maxWidth: '100%' } : null),
    };

    const titleInput = (
                <TextInput
                    style={resolvedInputStyle}
                    value={title}
                    placeholder={placeholder}
                    expandable={effectiveExpandable}
                    multiline={effectiveMultiline}
                    hybrid={hybrid}
                    width={useAutosize || isMobile || collapseUntilFocus ? '100%' : undefined}
                    autoFocus={effectiveReadOnly ? false : autoFocus}
                    autoSelect={effectiveReadOnly ? false : autoSelect}
                    readOnly={effectiveReadOnly}
                    onBlur={handleTitleBlur}
                    onChange={handleTitleChange}
                    onFocus={handleTitleFocus}
                />
    );

    return (
        <>
            <div
                ref={containerRef}
                className={[
                  'node-title-button-container',
                  useAutosize ? 'node-title-button-container--autosize' : '',
                  isCollapsed ? 'is-collapsed' : '',
                  isFocused ? 'is-focused' : '',
                ].filter(Boolean).join(' ')}
                style={{
                    width: useAutosize ? 'auto' : '100%',
                    minWidth: 0,
                }}
            >

                {useAutosize ? (
                  <div className="node-title-autosize">
                    <span
                      className="node-title-autosize-sizer"
                      aria-hidden="true"
                      style={{
                        fontSize: resolvedInputStyle.fontSize,
                        fontWeight: resolvedInputStyle.fontWeight,
                        paddingTop: resolvedInputStyle.paddingTop,
                        paddingBottom: resolvedInputStyle.paddingBottom,
                        paddingLeft: resolvedInputStyle.paddingLeft,
                      }}
                    >
                      {sizerText}
                    </span>
                    {titleInput}
                  </div>
                ) : titleInput}

            </div>
        </>
    );
};

// Dev Tools
NodeTitleButton.displayName = 'NodeTitleButton';

export default NodeTitleButton;
